import React, {useEffect, useState} from 'react';
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import {styled} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import AppDrawer from "../AppDrawer/AppDrawer";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Slider from '@mui/material/Slider';
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import {Box} from "@mui/material";
import {PieChart, useDrawingArea} from "@mui/x-charts";
import theme from "../../theme";

const PREFIX = 'MortgageCalculator';
const classes = {
   root: `${PREFIX}-root`,
   paper: `${PREFIX}-paper`,
   mortgageCalcContainer: `${PREFIX}-mortgageCalcContainer`,
}
const Root = styled('div')(({ theme }) => ({
   [`&.${classes.root}`]: {
      display: "flex",
      paddingTop: '64px'
   },
   [`& .${classes.paper}`]: {
      padding: theme.spacing(2),
      color: theme.palette.text.secondary,
      width: '100%'
   },
   [`& .${classes.mortgageCalcContainer}`]: {
      paddingTop: "40px"
   },
}))


export default function MortgageCalculator(props) {

   const [purchasePrice, setPurchasePrice] = useState(0);
   const [isDpDollars, setIsDpDollars] = useState(true);
   const [downpayment, setDownpayment] = useState(0);
   const [downpaymentPercent, setDownPaymentPercent] = useState(0);

   const [loanAmount, setLoanAmount] = useState(10000);
   const [annualInterestRate, setAnnualInterestRate] = useState(6.5);
   const [loanTerm, setLoanTerm] = useState(30);

   // Output
   const [monthlyPayment, setMonthlyPayment] = useState(0);
   const [principleAndInterestPayment, setPrincipleAndInterestPayment] = useState(0);
   const [monthlyTaxesAndInsurance, setMonthlyTaxesAndInsurance] = useState(0);
   const [monthlyHoa, setMonthlyHoa] = useState(0);
   const [annualPropertyTaxes, setAnnualPropertyTaxes] = useState(0);
   const [annualInsurance, setAnnualInsurance] = useState(0);
   const [pmi, setPmi] = useState(0);
   const [totalInterestPaid, setTotalInterestPaid] = useState(0);
   const [chartData, setChartData] = useState([]);

   const handleLoanTermChange = e => {
      setLoanTerm(parseInt(e.target.value));
   };

   const handleLoanAmountChange = e => {
      const loanAmount = parseFloat(e.target.value);
      isNaN(loanAmount) ? setLoanAmount(10000): setLoanAmount(loanAmount);
   };
   const handleLoanAmountSliderChange = (event, newValue) => {
      setLoanAmount(newValue);
   }

   const handleInterestRateChange = e => {
      setAnnualInterestRate(parseFloat(e.target.value));
   };
   const handleInterestRateSliderChange = (event, newValue) => {
      setAnnualInterestRate(newValue);
   };

   const handleInsuranceChange = e => {
      setAnnualInsurance(parseFloat(e.target.value));
   }
   const handleInsuranceSliderChange = (e, newValue) => {
      setAnnualInsurance(newValue);
   }

   const handleTaxesChange = e => {
      setAnnualPropertyTaxes(parseFloat(e.target.value));
   }
   const handleTaxesSliderChange = (e, newValue) => {
      setAnnualPropertyTaxes(newValue);
   }

   const handleHoaFeesChange = e => {
      setMonthlyHoa(parseFloat(e.target.value));
   }
   const handleHoaFeesSliderChange = (e, newValue) => {
      setMonthlyHoa(newValue);
   }


   useEffect(() => {
      // MonthlyPayment = P [ i(1 + i)^n ] / [ (1 + i)^n – 1]
      // P = principle
      // i = interest rate / 12
      // n = number of monthly payments
      if(loanAmount > 0 && annualInterestRate > 0 && loanTerm > 0) {
         // Main calculation
         const monthlyInsurance = annualInsurance / 12;
         const monthlyTaxes = annualPropertyTaxes / 12;
         const loanTermMonths = loanTerm * 12;
         const monthlyInterestRate = annualInterestRate / 100 / 12;
         const pAndIPayment = getMonthlyPrincipalAndInterestPayment(loanAmount, monthlyInterestRate, loanTermMonths);

         setMonthlyTaxesAndInsurance(monthlyInsurance + monthlyTaxes);
         setPrincipleAndInterestPayment(pAndIPayment);
         setMonthlyPayment(pAndIPayment + monthlyTaxes + monthlyInsurance + monthlyHoa);
         setChartData([
            {label: 'Principle & Interest', value: pAndIPayment},
            {label: 'Taxes & Insurance', value: monthlyTaxes + monthlyInsurance},
            {label: 'HOA', value: monthlyHoa}
         ]);
      }
   }, [loanAmount, annualInterestRate, loanTerm, annualInsurance, annualPropertyTaxes, monthlyHoa]);


   // Calculate total interest paid
   useEffect(() => {
      if(loanAmount > 0 && monthlyPayment > 0 && loanTerm > 0) {
         const loanTermMonths = loanTerm * 12;
         const totalPaid = monthlyPayment * loanTermMonths;
         const totalInterest = totalPaid - loanAmount;
         setTotalInterestPaid(totalInterest);
      }
   }, [monthlyPayment, loanAmount, loanTerm])

   const loanTermChoices = [
      {
         value: 30,
         label: '30 Year Fixed',
      },
      {
         value: 20,
         label: '20 Year Fixed',
      },
      {
         value: 15,
         label: '15 Year Fixed',
      }
   ];

   function getMonthlyPrincipalAndInterestPayment(loanAmount, monthlyInterestRate, loanTermMonths) {
      return (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, loanTermMonths)) /
         (Math.pow(1 + monthlyInterestRate, loanTermMonths) - 1);
   }

   const StyledText = styled('text')(({ theme }) => ({
      fill: theme.palette.text.primary,
      textAnchor: 'middle',
      dominantBaseline: 'central',
      fontSize: 20,
   }));

   function PieCenterLabel() {
      const { width, height, left, top } = useDrawingArea();
      return (
         <StyledText
            x={left + width / 2}
            y={top + height / 2}
            textAnchor="middle"
            dominantBaseline="middle" // Use 'middle' for vertical centering
         >
            <tspan
               x={left + width / 2} // Ensure it's also centered horizontally
               dy="-0.3em"
               fontSize={18} // Adjust emphasized font size
               fontWeight={500}
            >
               Monthly Payment
            </tspan>

            <tspan
               x={left + width / 2} // Ensure it's also centered horizontally
               dy="1.8em"
               fontSize={18} // Adjust emphasized font size
               fontWeight={600}
            >
               ${monthlyPayment.toLocaleString('en-US', {
               minimumFractionDigits: 2,
               maximumFractionDigits: 2
            })}
            </tspan>
         </StyledText>
      );
   }

   // Use the styles

   const chartSize = {
      width: 350,
      height: 350,
   };


   return (
      <Root className={classes.root}>
         <AppDrawer/>
         <Paper className={classes.paper}>
            <Typography variant="h4">Mortgage Calculator</Typography>
            <Grid container rowSpacing={2} columnSpacing={2} className={classes.mortgageCalcContainer}>
               <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{m:5}}>
                     <Grid container rowSpacing={2} columnSpacing={2}>
                        <Grid size={{ sm: 12 }}>
                           <FormControl fullWidth sx={{mb: 2}}>
                              <TextField
                                 id="outlined-select-term"
                                 select
                                 label="Loan Term"
                                 defaultValue='30'
                                 value={loanTerm}
                                 onChange={handleLoanTermChange}
                                 fullWidth
                              >
                                 {loanTermChoices.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                       {option.label}
                                    </MenuItem>
                                 ))}
                              </TextField>
                           </FormControl>
                        </Grid>

                        <Grid size={{ sm: 12 }}>
                           <FormControl fullWidth variant="outlined" sx={{mb: 3}}>
                              <InputLabel htmlFor="outlined-adornment-amount">Loan Amount</InputLabel>
                              <OutlinedInput
                                 label="Loan Amount"
                                 id="outlined-adornment-amount"
                                 type='number'
                                 inputProps={{min:10000, max: 3000000, step: 5000, 'pattern':'[0-9]*'}}
                                 value={loanAmount}
                                 onChange={handleLoanAmountChange}
                              />
                              <Slider
                                 value={loanAmount}
                                 aria-label="Loan Amount"
                                 step={5000}
                                 min={10000}
                                 max={3000000}
                                 valueLabelDisplay="auto"
                                 onChange={handleLoanAmountSliderChange}
                                 color="secondary"
                              />
                           </FormControl>
                        </Grid>

                     </Grid>
                     <Grid size={12}>
                        <FormControl fullWidth variant="outlined" sx={{mb: 2}}>
                           <InputLabel htmlFor="outlined-adornment-amount">Interest Rate</InputLabel>
                           <OutlinedInput
                              label="Interest Rate"
                              id="outlined-adornment-amount"
                              type='number'
                              inputProps={{min:0, max: 20, step: .25, 'pattern':'[0-9]*'}}
                              value={annualInterestRate}
                              onChange={handleInterestRateChange}
                           />
                           <Slider
                              aria-label="Rate"
                              value={annualInterestRate}
                              step={0.25}
                              min={0}
                              max={20}
                              valueLabelDisplay="auto"
                              onChange={handleInterestRateSliderChange}
                              color="secondary"
                           />
                        </FormControl>
                     </Grid>
                     <Grid size={12}>
                        <FormControl fullWidth variant="outlined" sx={{mb: 2}}>
                           <InputLabel htmlFor="outlined-adornment-amount">Annual Homeowners Insurance</InputLabel>
                           <OutlinedInput
                              label="Annual Homeowners Insurance"
                              id="outlined-adornment-amount"
                              type='number'
                              inputProps={{min:0, max: 200000, step: 25, 'pattern':'[0-9]*'}}
                              value={annualInsurance}
                              onChange={handleInsuranceChange}
                           />
                           <Slider
                              aria-label="Annual Homeowners Insurance"
                              value={annualInsurance}
                              step={25}
                              min={0}
                              max={200000}
                              valueLabelDisplay="auto"
                              onChange={handleInsuranceSliderChange}
                              color="secondary"
                           />
                        </FormControl>
                     </Grid>
                     <Grid size={12}>
                        <FormControl fullWidth variant="outlined" sx={{mb: 2}}>
                           <InputLabel htmlFor="outlined-adornment-amount">Annual Property Taxes</InputLabel>
                           <OutlinedInput
                              label="Annual Property Taxes"
                              id="outlined-adornment-amount"
                              type='number'
                              inputProps={{min:0, max: 200000, step: 25, 'pattern':'[0-9]*'}}
                              value={annualPropertyTaxes}
                              onChange={handleTaxesChange}
                           />
                           <Slider
                              aria-label="Homeowners Insurance"
                              value={annualPropertyTaxes}
                              step={25}
                              min={0}
                              max={200000}
                              valueLabelDisplay="auto"
                              onChange={handleTaxesSliderChange}
                              color="secondary"
                           />
                        </FormControl>
                     </Grid>
                     <Grid size={12}>
                        <FormControl fullWidth variant="outlined" sx={{mb: 2}}>
                           <InputLabel htmlFor="outlined-adornment-amount">Monthly HOA Fees</InputLabel>
                           <OutlinedInput
                              label="Monthly HOA Fees"
                              id="outlined-adornment-amount"
                              type='number'
                              inputProps={{min:0, max: 10000, step: 5, 'pattern':'[0-9]*'}}
                              value={monthlyHoa}
                              onChange={handleHoaFeesChange}
                           />
                           <Slider
                              aria-label="Homeowners Insurance"
                              value={monthlyHoa}
                              step={5}
                              min={0}
                              max={10000}
                              valueLabelDisplay="auto"
                              onChange={handleHoaFeesSliderChange}
                              color="secondary"
                           />
                        </FormControl>
                     </Grid>
                  </Box>
               </Grid>

               <Grid size={{ xs: 12, sm: 6 }} sx={{background: '#f5f5f5', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                  <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                     <PieChart
                        width={300}
                        height={300}
                        margin={{ left: 15, right:15 }}
                        slotProps={{ legend: { hidden: true } }}
                        series={[
                           {
                              innerRadius: 100,
                              data: chartData,
                           },
                        ]}>
                        <PieCenterLabel/>
                     </PieChart>
                  </Box>

                  <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                     <Box sx={{padding: "15px"}}>
                        <Typography  variant="h5" sx={{fontSize: "24px", color: theme.palette.primary.main }}>Principle and Interest</Typography>
                        <Typography sx={{fontSize: "20px", fontWeight: 500}}>${principleAndInterestPayment.toLocaleString('en-US', {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2
                        })}
                        </Typography>
                     </Box>
                     <Box sx={{padding: "15px"}}>
                        <Typography variant="h5" sx={{fontSize: "24px", color: theme.palette.primary.main}}>Taxes and Insurance</Typography>
                        <Typography sx={{fontSize: "20px", fontWeight: 500}}>${monthlyTaxesAndInsurance.toLocaleString('en-US', {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2
                        })}
                        </Typography>
                     </Box>
                     <Box sx={{padding: "15px"}}>
                        <Typography sx={{fontSize: "24px", color: theme.palette.primary.main}}>Hoa fees</Typography>
                        <Typography sx={{fontSize: "20px", fontWeight: 500}}>${monthlyHoa.toLocaleString('en-US', {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2
                        })}
                        </Typography>
                     </Box>
                     <Box sx={{padding: "15px"}}>
                        <Typography sx={{fontSize: "24px", color: theme.palette.primary.main}}>Total Interest Paid</Typography>
                        <Typography sx={{fontSize: "20px", fontWeight: 500}}>${totalInterestPaid.toLocaleString('en-US', {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2
                        })}
                        </Typography>
                     </Box>
                  </Box>

               </Grid>
            </Grid>


         </Paper>
      </Root>
   );
}